import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  MessageSquare, Calendar, User, Briefcase, FileText, Search,
  Filter, Edit2, Trash2, Eye, Loader2, ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { notesAPI } from '../../services/api';
import { toast } from 'sonner';

const NotesSummaryTab = ({ onViewEntity }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    fetchAllNotes();
  }, []);

  const fetchAllNotes = async () => {
    try {
      setLoading(true);
      const response = await notesAPI.getAll();
      setNotes(response.data.notes || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Archive this note?')) return;
    try {
      await notesAPI.delete(noteId);
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Note archived');
    } catch (error) {
      toast.error('Failed to archive note');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEntityIcon = (type) => {
    const icons = {
      booking: Calendar,
      worker: User,
      job: Briefcase,
      application: FileText
    };
    return icons[type] || FileText;
  };

  const getEntityColor = (type) => {
    const colors = {
      booking: 'bg-[#D4AF37]/10 text-[#D4AF37] border-blue-300',
      worker: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      job: 'bg-[#F4C2C2]/15 text-purple-700 border-purple-300',
      application: 'bg-amber-100 text-amber-700 border-amber-300'
    };
    return colors[type] || 'bg-[var(--background-subtle)] text-[var(--text-primary)] border-[var(--border-primary)]';
  };

  // Filter and sort notes
  const filteredNotes = notes
    .filter(note => {
      if (filterType !== 'all' && note.entity_type !== filterType) return false;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          note.content.toLowerCase().includes(search) ||
          note.entity_name?.toLowerCase().includes(search) ||
          note.created_by_name?.toLowerCase().includes(search)
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.created_at) - new Date(a.created_at);
      } else if (sortBy === 'oldest') {
        return new Date(a.created_at) - new Date(b.created_at);
      } else if (sortBy === 'type') {
        return a.entity_type.localeCompare(b.entity_type);
      }
      return 0;
    });

  // Group notes by entity for summary view
  const groupedByEntity = filteredNotes.reduce((acc, note) => {
    const key = `${note.entity_type}_${note.entity_id}`;
    if (!acc[key]) {
      acc[key] = {
        entity_type: note.entity_type,
        entity_id: note.entity_id,
        entity_name: note.entity_name,
        notes: []
      };
    }
    acc[key].notes.push(note);
    return acc;
  }, {});

  const entityGroups = Object.values(groupedByEntity);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Notes Summary</h1>
        <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
          <MessageSquare className="w-4 h-4" />
          {notes.length} total notes
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search notes..."
              className="w-full pl-10 pr-4 py-2 border border-[var(--border-primary)] rounded-lg focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)]"
              data-testid="notes-search"
            />
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[var(--text-muted)]" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-[var(--border-primary)] rounded-lg focus:ring-2 focus:ring-[var(--primary)]"
              data-testid="notes-filter-type"
            >
              <option value="all">All Types</option>
              <option value="booking">Bookings</option>
              <option value="worker">Workers</option>
              <option value="job">Jobs</option>
              <option value="application">Applications</option>
            </select>
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-[var(--border-primary)] rounded-lg focus:ring-2 focus:ring-[var(--primary)]"
            data-testid="notes-sort"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="type">By Type</option>
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {['booking', 'worker', 'job', 'application'].map(type => {
          const count = notes.filter(n => n.entity_type === type).length;
          const Icon = getEntityIcon(type);
          return (
            <div
              key={type}
              className={`card p-4 cursor-pointer transition-all hover:shadow-md ${filterType === type ? 'ring-2 ring-[var(--primary)]' : ''}`}
              onClick={() => setFilterType(filterType === type ? 'all' : type)}
              data-testid={`notes-stat-${type}`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getEntityColor(type)}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs text-[var(--text-muted)] capitalize">{type} Notes</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Notes by Entity */}
      {entityGroups.length === 0 ? (
        <div className="card p-12 text-center">
          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-[var(--text-muted)]">No notes found</p>
          <p className="text-sm text-gray-400">Notes will appear here when you add them to bookings, workers, or jobs</p>
        </div>
      ) : (
        <div className="space-y-4">
          {entityGroups.map((group) => {
            const Icon = getEntityIcon(group.entity_type);
            return (
              <div key={`${group.entity_type}_${group.entity_id}`} className="card overflow-hidden" data-testid={`notes-group-${group.entity_id}`}>
                {/* Entity Header */}
                <div className={`px-4 py-3 flex items-center justify-between ${getEntityColor(group.entity_type)} bg-opacity-50`}>
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5" />
                    <div>
                      <p className="font-semibold capitalize">{group.entity_type}</p>
                      <p className="text-sm opacity-80">{group.entity_name || group.entity_id.substring(0, 8) + '...'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{group.notes.length} note(s)</span>
                    {onViewEntity && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewEntity(group.entity_type, group.entity_id)}
                        data-testid={`view-entity-${group.entity_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    )}
                  </div>
                </div>

                {/* Notes List */}
                <div className="p-4 space-y-3">
                  {group.notes.slice(0, 3).map((note) => (
                    <div
                      key={note.id}
                      className="bg-[var(--background-subtle)] rounded-lg p-3 hover:bg-[var(--background-subtle)] transition-colors"
                    >
                      <p className="text-sm text-[var(--text-primary)] line-clamp-2">{note.content}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-[var(--text-muted)]">
                          {note.created_by_name || 'Owner'} • {formatDate(note.created_at)}
                          {note.updated_at && <span className="text-[#D4AF37] ml-1">(edited)</span>}
                        </span>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="p-1 text-gray-400 hover:text-[var(--error)] transition-colors"
                          title="Archive"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {group.notes.length > 3 && (
                    <button
                      className="w-full text-center py-2 text-sm text-[var(--primary)] hover:underline flex items-center justify-center gap-1"
                      onClick={() => onViewEntity && onViewEntity(group.entity_type, group.entity_id)}
                    >
                      View all {group.notes.length} notes
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default NotesSummaryTab;
